/**
 * Lê a abertura do caso (E126.F3) e relê enquanto o material ainda está sendo lido — a leitura
 * por imagem, a classificação das imagens e a conferência facial correm em segundo plano depois
 * do envio. A rota só lê o que está gravado, então reler não custa leitura de documento.
 *
 * E177.F2 (GUS-253 A3): o pacote real (o ZIP do Safra, com leitura por imagem e conferência facial)
 * passa dos ~5 min do teto antigo, e a tela ficava em "Verificação em andamento" até recarregar.
 * Agora relê depressa no começo e devagar depois, até 1 h; e o erro de rede de uma releitura não a
 * interrompe (a próxima tenta de novo).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { AxiosInstance } from 'axios';

import { lerAberturaDoCaso, type AberturaDoCasoData } from '@/lib/abertura_do_caso';

export const INTERVALO_DE_RELEITURA_MS = 3000;
/** Depois das releituras rápidas, o intervalo longo — a leitura que demora ainda chega à tela. */
export const INTERVALO_LONGO_MS = 15000;
/** As releituras rápidas (~5 min). */
export const RELEITURAS_RAPIDAS = 100;
/** Teto das releituras (~1 h no total): passado dele, a tela mostra o que tem. */
export const MAXIMO_DE_RELEITURAS = RELEITURAS_RAPIDAS + 220;

/** O intervalo até a próxima releitura. */
export function intervaloDaReleitura(feitas: number): number {
  return feitas < RELEITURAS_RAPIDAS ? INTERVALO_DE_RELEITURA_MS : INTERVALO_LONGO_MS;
}

export interface EstadoDaAbertura {
  dados: AberturaDoCasoData | null;
  erro: boolean;
}

/** E134.F2: o estado e a releitura sob pedido (depois de conferir a custódia, por exemplo). */
export interface AberturaComReleitura extends EstadoDaAbertura {
  recarregar: () => Promise<void>;
}

export function useAberturaDoCaso(
  client: AxiosInstance,
  caseId: string,
  { releitura }: { releitura: boolean },
): AberturaComReleitura {
  const [estado, setEstado] = useState<EstadoDaAbertura>({ dados: null, erro: false });
  const releituras = useRef(0);

  const ler = useCallback(async (): Promise<AberturaDoCasoData | null> => {
    try {
      const dados = await lerAberturaDoCaso(client, caseId);
      setEstado({ dados, erro: false });
      return dados;
    } catch {
      setEstado((anterior) => ({ dados: anterior.dados, erro: true }));
      return null;
    }
  }, [client, caseId]);

  useEffect(() => {
    let ativo = true;
    let espera: ReturnType<typeof setTimeout> | undefined;
    releituras.current = 0;
    const ciclo = async (): Promise<void> => {
      const dados = await ler();
      // A leitura que falhou (rede) não diz que terminou: relê de novo. Só para quando o material
      // está lido, ou no teto.
      if (!ativo || !releitura || (dados !== null && !dados.composicao.em_leitura)) return;
      if (releituras.current >= MAXIMO_DE_RELEITURAS) return;
      const intervalo = intervaloDaReleitura(releituras.current);
      releituras.current += 1;
      espera = setTimeout(() => void ciclo(), intervalo);
    };
    void ciclo();
    return () => {
      ativo = false;
      if (espera !== undefined) clearTimeout(espera);
    };
  }, [ler, releitura]);

  const recarregar = useCallback(async (): Promise<void> => {
    await ler();
  }, [ler]);

  return { ...estado, recarregar };
}
